import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * PANDAMO — sincronización ligera del carrito Horizon
 *
 * Objetivo:
 * - NO tocar /cart/change, /cart/update ni /cart/clear.
 * - Mantener adds personalizados sincronizados con Horizon.
 * - Actualizar únicamente los elementos visuales PANDAMO.
 * - Sin polling continuo.
 */

if (!window.__pandamoCartSyncInstalled) {
  window.__pandamoCartSyncInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.__pandamoDirectFetch = originalFetch;
  const root = window.Shopify?.routes?.root || '/';

  let visualRefreshTimer = null;
  let lastVisualSyncAt = 0;
  let drawerRefreshTimer = null;

  const scheduleDrawerRefresh = () => {
    window.clearTimeout(drawerRefreshTimer);
    drawerRefreshTimer = window.setTimeout(() => {
      window.__pandamoRefreshCartDrawer?.({ reason: 'add-completed' })
        .catch((error) => console.warn('[PANDAMO cart refresh]', error));
    }, 180);
  };

  const getUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input instanceof Request) return input.url;
    return '';
  };

  const getMethod = (input, init) => {
    const method = init?.method || (input instanceof Request ? input.method : 'GET');
    return String(method).toUpperCase();
  };

  const getPathname = (input) => {
    try {
      return new URL(getUrl(input), window.location.origin).pathname;
    } catch (_error) {
      return '';
    }
  };

  const isCartAddRequest = (input, init) =>
    getMethod(input, init) === 'POST' &&
    /\/cart\/add(?:\.js)?\/?$/.test(getPathname(input));

  const getSectionIds = () =>
    [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean)
      .filter((sectionId, index, all) => all.indexOf(sectionId) === index)
      .slice(0, 4);

  const readLineData = (body) => {
    let variantId = '';
    let quantity = 1;

    const readObject = (data) => {
      const firstItem = Array.isArray(data?.items) ? data.items[0] : null;
      variantId = String(firstItem?.id || data?.id || '');
      quantity = Number(firstItem?.quantity || data?.quantity || 1) || 1;
    };

    if (body instanceof FormData || body instanceof URLSearchParams) {
      variantId = String(body.get('id') || '');
      quantity = Number(body.get('quantity') || 1) || 1;
    } else if (typeof body === 'string') {
      try {
        readObject(JSON.parse(body));
      } catch (_error) {
        const params = new URLSearchParams(body);
        variantId = String(params.get('id') || '');
        quantity = Number(params.get('quantity') || 1) || 1;
      }
    }

    return { variantId, quantity };
  };

  const addSectionsToBody = (body, sectionIds) => {
    const sections = sectionIds.join(',');

    if (body instanceof FormData) {
      if (body.has('sections')) return { body, alreadyNative: true };
      const next = new FormData();
      body.forEach((value, key) => next.append(key, value));
      next.set('sections', sections);
      return { body: next, alreadyNative: false };
    }

    if (body instanceof URLSearchParams) {
      if (body.has('sections')) return { body, alreadyNative: true };
      const next = new URLSearchParams(body);
      next.set('sections', sections);
      return { body: next, alreadyNative: false };
    }

    if (typeof body === 'string') {
      try {
        const data = JSON.parse(body);
        if (data.sections) return { body, alreadyNative: true };
        data.sections = sections;
        return { body: JSON.stringify(data), alreadyNative: false };
      } catch (_error) {
        const params = new URLSearchParams(body);
        if (params.has('sections')) return { body, alreadyNative: true };
        params.set('sections', sections);
        return { body: params.toString(), alreadyNative: false };
      }
    }

    return { body, alreadyNative: true };
  };

  const getCart = async () => {
    const response = await originalFetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) throw new Error('No se pudo consultar el carrito');
    return response.json();
  };

  const formatMoney = (amountInCents) => {
    const currency = window.Shopify?.currency?.active || 'MXN';

    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format((Number(amountInCents) || 0) / 100);
    } catch (_error) {
      return `$${((Number(amountInCents) || 0) / 100).toFixed(2)}`;
    }
  };

  const syncPandamoCartVisual = (cart) => {
    if (!cart) return;

    lastVisualSyncAt = Date.now();

    const cartItems = Array.isArray(cart.items)
      ? cart.items
      : Array.isArray(cart.lines)
        ? cart.lines
        : [];

    const summedItemCount = cartItems.reduce(
      (total,item) => total + (Number(item?.quantity) || 0),
      0
    );

    const itemCount = Number(
      cart.item_count ??
      cart.itemCount ??
      cart.totalQuantity ??
      summedItemCount
    ) || 0;

    const eligibleItemCount = cartItems.reduce((total, item) => {
      const properties = item?.properties || item?.attributes || {};
      const isUpsell = String(properties?._pandamo_cart_upsell || '') === '1';
      const isExcluded = String(properties?._pandamo_bundle_excluded || '') === '1';
      return (isUpsell || isExcluded)
        ? total
        : total + (Number(item?.quantity) || 0);
    }, 0);

    let cartTotal = Number(
      cart.total_price ??
      cart.totalPrice ??
      cart.total_price_cents
    ) || 0;

    if (!cartTotal && cart?.cost?.totalAmount?.amount != null) {
      cartTotal = Math.round(Number(cart.cost.totalAmount.amount) * 100) || 0;
    }

    const getBundleStatus = () => {
      if (eligibleItemCount <= 0) return '';

      const stageIndex = Math.min(2, Math.floor((eligibleItemCount - 1) / 3));
      const stageNumber = stageIndex + 1;
      const take = stageNumber * 3;
      const pay = stageNumber;
      const position = ((eligibleItemCount - 1) % 3) + 1;
      const missing = Math.max(0, 3 - position);

      if (eligibleItemCount >= 9) return '9×3 COMPLETO';
      if (missing > 0) return `${take}×${pay} ACTIVADO · agrega ${missing} más`;
      return `${take}×${pay} COMPLETO · agrega 1 y activa ${take + 3}×${pay + 1}`;
    };

    const bundleStatus = getBundleStatus();

    document.querySelectorAll('[data-pandamo-line-bundle]').forEach((badge) => {
      if (badge.dataset.pandamoBundleExcluded === 'true') {
        badge.textContent =
          badge.dataset.pandamoBundleLabel ||
          'Precio especial · no aplica en 3×1';
        return;
      }

      if (bundleStatus) badge.textContent = bundleStatus;
    });

    document.querySelectorAll('[data-pandamo-cart-visual]').forEach((visual) => {
      const shippingGoal = Number(visual.dataset.pandamoShippingGoal) || 119700;
      const remaining = Math.max(0, shippingGoal - cartTotal);
      const progress = shippingGoal > 0
        ? Math.min(100, Math.max(0, Math.round((cartTotal * 100) / shippingGoal)))
        : 100;

      visual.hidden = false;
      visual.removeAttribute('hidden');
      visual.dataset.cartCount = String(itemCount);
      visual.dataset.pandamoEligibleCount = String(eligibleItemCount);

      const shippingBar = visual.querySelector('.pandamo-shipping-bar');
      const message = visual.querySelector('[data-pandamo-shipping-message]');
      const percent = visual.querySelector('[data-pandamo-shipping-percent]');
      const progressbar = visual.querySelector('[data-pandamo-shipping-progress]');
      const fill = visual.querySelector('[data-pandamo-shipping-progress-fill]');
      const tip = visual.querySelector('[data-pandamo-shipping-tip]');
      const hasFreeShipping = remaining <= 0;

      shippingBar?.classList.toggle('is-free-shipping', hasFreeShipping);

      if (message) {
        message.innerHTML = hasFreeShipping
          ? '<strong>✓ YA TIENES ENVÍO GRATIS 🚚</strong>'
          : '<strong>ENVÍO GRATIS DESDE $1,197 🚚</strong><br><span>Te faltan <b>' + formatMoney(remaining) + '</b> para desbloquearlo</span>';
      }

      if (tip) {
        tip.innerHTML = hasFreeShipping
          ? '<strong>✓ Listo.</strong> Ya desbloqueaste tu envío gratis.'
          : '<strong>Consejo PANDAMO 👀</strong> No deslices hasta el final del carrito si no quieres encontrar el perfume <b>Imposible de ignorar</b>... casi regalado.';
      }

      if (percent) percent.textContent = `${progress}%`;
      if (progressbar) progressbar.setAttribute('aria-valuenow', String(progress));
      if (fill) fill.style.setProperty('--pandamo-shipping-progress', `${progress}%`);
    });

    document.querySelectorAll('[data-pandamo-cart-item-count]').forEach((count) => {
      count.textContent = String(itemCount);
    });

    document.dispatchEvent(
      new CustomEvent('pandamo:cart-synced', { detail: { cart } })
    );
  };

  const refreshVisual = async () => {
    const cart = await getCart();
    syncPandamoCartVisual(cart);
    return cart;
  };

  const scheduleVisualRefresh = (delay = 260) => {
    window.clearTimeout(visualRefreshTimer);

    visualRefreshTimer = window.setTimeout(() => {
      if (Date.now() - lastVisualSyncAt < 220) return;
      refreshVisual().catch(() => {});
    }, delay);
  };

  /**
   * Only /cart/add is wrapped so custom buttons can hydrate Horizon.
   * Native remove/change requests pass through untouched.
   */
  window.fetch = async function pandamoCartFetch(input, init) {
    if (!isCartAddRequest(input, init)) {
      return originalFetch(input, init);
    }

    const sectionIds = getSectionIds();

    if (!sectionIds.length || input instanceof Request) {
      const response = await originalFetch(input, init);
      if (response.ok) scheduleDrawerRefresh();
      return response;
    }

    const originalBody = init?.body;
    const prepared = addSectionsToBody(originalBody, sectionIds);

    if (prepared.alreadyNative) {
      const response = await originalFetch(input, init);
      if (response.ok) scheduleDrawerRefresh();
      return response;
    }

    const { variantId, quantity } = readLineData(originalBody);
    const nextInit = { ...(init || {}), body: prepared.body };
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await originalFetch(input, nextInit);
      const addResult = await response.clone().json().catch(() => null);

      if (!response.ok || addResult?.status) {
        deferred.reject(
          new Error(addResult?.description || addResult?.message || 'No se pudo agregar al carrito')
        );
        return response;
      }

      const cart = await getCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: quantity,
          sections: addResult?.sections || {},
          source: 'pandamo-cart-sync',
          sourceId: 'pandamo-cart-sync',
          didError: false,
        },
      });

      syncPandamoCartVisual(cart);
      scheduleDrawerRefresh();
      return response;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  };

  /**
   * Native Horizon remove/change already provides the post-mutation cart
   * through CartLinesUpdateEvent. We consume it passively — no extra morph,
   * no extra cart mutation, no racing request.
   */

  const updateHeaderCartBubble = (cart) => {
    const count = Number(cart?.item_count) || 0;

    document.querySelectorAll('cart-icon').forEach((icon) => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

      const bubble = icon.querySelector('.cart-bubble');
      const countEl = icon.querySelector('.cart-bubble__text-count');

      if (bubble) {
        bubble.classList.toggle('visually-hidden', count <= 0);
        if (count <= 99) bubble.setAttribute('data-maintain-ratio','');
        else bubble.removeAttribute('data-maintain-ratio');
      }

      if (countEl) {
        countEl.classList.toggle('hidden', count <= 0);
        countEl.textContent = count > 99 ? '' : String(count);
      }
    });
  };

  const replaceDrawerInnerFromSection = (sectionHtml) => {
    if (!sectionHtml) return false;

    const parsed = new DOMParser().parseFromString(sectionHtml,'text/html');
    const nextInner = parsed.querySelector('.cart-drawer__inner');
    const currentInner = document.querySelector('#cart-drawer .cart-drawer__inner');

    if (!nextInner || !currentInner) return false;

    const oldScroller = currentInner.querySelector('.cart-drawer__content');
    const oldScrollTop = oldScroller ? oldScroller.scrollTop : 0;
    const currentVisual = currentInner.querySelector('[data-pandamo-cart-visual]');

    const imported = document.importNode(nextInner,true);
    const importedVisual = imported.querySelector('[data-pandamo-cart-visual]');

    /* Keep the existing live bar node instead of swapping it for a stale
       Section Rendering copy. This prevents the shipping/promo bars from
       disappearing when the same upsell line goes from qty 1 → 2 → 3. */
    if (currentVisual) {
      if (importedVisual) {
        importedVisual.replaceWith(currentVisual);
      } else {
        const cartItems = imported.querySelector('cart-items-component');
        if (cartItems) imported.insertBefore(currentVisual,cartItems);
        else imported.appendChild(currentVisual);
      }
    }

    currentInner.replaceWith(imported);

    const newScroller = imported.querySelector('.cart-drawer__content');

    requestAnimationFrame(() => {
      if (newScroller && oldScrollTop > 0) {
        newScroller.scrollTop = Math.min(oldScrollTop,newScroller.scrollHeight);
      }

      /* The HTML returned by a section render can race another cart event.
         Re-read the real Ajax cart after the replacement so the shipping bar
         is never left hidden or stale. */
      refreshVisual().catch(() => {});
    });

    return true;
  };

  /* PANDAMO QUICK CART V12 START */
  const quickCartPending = new Map();
  let quickCartFlushTimer = null;
  let quickCartInFlight = false;
  let quickCartNeedsFreshRender = false;

  const getQuickCartRowByKey = (key) =>
    Array.from(document.querySelectorAll('#cart-drawer .cart-items__table-row'))
      .find((row) => String(row.dataset.key || '') === String(key || ''));

  const setQuickRowPending = (key, quantity) => {
    const row = getQuickCartRowByKey(key);
    if (!row) return;

    row.dataset.pandamoQuickPending = 'true';

    if (Number(quantity) <= 0) {
      row.dataset.pandamoQuickRemoving = 'true';
      row.style.display = 'none';
    }
  };

  const fetchFreshQuickCartDrawer = async () => {
    const sectionResponse = await originalFetch(
      root + '?sections=cart-drawer-section&pandamo_quick=' + Date.now(),
      {
        credentials:'same-origin',
        headers:{Accept:'application/json'},
        cache:'no-store'
      }
    );

    if (!sectionResponse.ok) return false;
    const payload = await sectionResponse.json();

    return replaceDrawerInnerFromSection(
      payload?.['cart-drawer-section']
    );
  };

  const scheduleQuickCartFlush = (delay = 65) => {
    window.clearTimeout(quickCartFlushTimer);
    quickCartFlushTimer = window.setTimeout(() => {
      flushQuickCart().catch(() => {});
    }, delay);
  };

  const flushQuickCart = async () => {
    if (quickCartInFlight) {
      quickCartNeedsFreshRender = true;
      return;
    }

    if (!quickCartPending.size) return;

    quickCartInFlight = true;

    const batch = Array.from(quickCartPending.entries());
    quickCartPending.clear();

    const updates = {};
    batch.forEach(([key, value]) => {
      updates[String(key)] = Math.max(0, Number(value.quantity) || 0);
    });

    try {
      const response = await originalFetch(`${root}cart/update.js`,{
        method:'POST',
        credentials:'same-origin',
        headers:{
          'Content-Type':'application/json',
          'Accept':'application/json'
        },
        body:JSON.stringify({
          updates,
          sections:'cart-drawer-section',
          sections_url:window.location.pathname
        })
      });

      const cart = await response.json();

      if (!response.ok || cart?.errors) {
        throw new Error(cart?.errors || 'No se pudo actualizar el carrito');
      }

      updateHeaderCartBubble(cart);
      syncPandamoCartVisual(cart);

      /*
        If the customer already made another change while this request was
        travelling, do NOT render this older HTML over their newer taps.
        Flush the next batch first and render only the newest server state.
      */
      if (quickCartPending.size) {
        quickCartNeedsFreshRender = true;
      } else {
        const sectionHtml = cart?.sections?.['cart-drawer-section'];
        let updated = false;

        if (sectionHtml) {
          updated = replaceDrawerInnerFromSection(sectionHtml);
        }

        if (!updated || quickCartNeedsFreshRender) {
          quickCartNeedsFreshRender = false;
          await fetchFreshQuickCartDrawer();
        }
      }
    } catch (error) {
      console.error('[PANDAMO quick cart]',error);
      quickCartNeedsFreshRender = false;

      /* Restore from the real server state if any optimistic change failed. */
      await fetchFreshQuickCartDrawer().catch(() => {});
      refreshVisual().catch(() => {});
    } finally {
      quickCartInFlight = false;

      if (quickCartPending.size) {
        scheduleQuickCartFlush(0);
      } else {
        document.querySelectorAll('#cart-drawer [data-pandamo-quick-pending]')
          .forEach((row) => {
            row.removeAttribute('data-pandamo-quick-pending');
            row.removeAttribute('data-pandamo-quick-removing');
          });
      }
    }
  };

  window.__pandamoQuickCartChange = ({ key, quantity, row } = {}) => {
    const itemKey = String(key || row?.dataset?.key || '');
    if (!itemKey) return false;

    const nextQuantity = Math.max(0, Number(quantity) || 0);

    quickCartPending.set(itemKey,{
      quantity:nextQuantity,
      at:Date.now()
    });

    setQuickRowPending(itemKey,nextQuantity);

    /*
      65ms is short enough to feel instant but still combines:
      - several + taps on one perfume
      - + taps on different perfumes
      - multiple trash taps
      into one Shopify request whenever they happen together.
    */
    scheduleQuickCartFlush(65);
    return true;
  };

  const fastRemoveLine = async (_line, button) => {
    const row = button?.closest('.cart-items__table-row');
    const key = row?.dataset?.key;

    if (!key) return false;

    if (button) {
      button.classList.add('is-pandamo-removing');
    }

    return window.__pandamoQuickCartChange({
      key,
      quantity:0,
      row
    });
  };
  /* PANDAMO QUICK CART V12 END */

  /* PANDAMO CANONICAL DRAWER REFRESH V13
     External add flows (decants, popup upsells, etc.) can bypass Horizon's
     normal cart event pipeline. If the drawer is currently in its empty
     markup, updating only totals/visuals is not enough: the product rows must
     be re-rendered from the live cart section. */
  const waitForQuickCartIdle = async (timeout = 1400) => {
    if (quickCartPending.size && !quickCartInFlight) {
      window.clearTimeout(quickCartFlushTimer);
      await flushQuickCart().catch(() => {});
    }

    const started = Date.now();
    while ((quickCartInFlight || quickCartPending.size) && Date.now() - started < timeout) {
      await new Promise((resolve) => window.setTimeout(resolve,25));
    }
  };

  let canonicalDrawerRefreshPromise = null;
  let canonicalDrawerRefreshPending = false;

  window.__pandamoRefreshCartDrawer = async ({ reason = 'external-add' } = {}) => {
    /* Merge simultaneous refresh calls into one request. */
    if (canonicalDrawerRefreshPromise) {
      canonicalDrawerRefreshPending = true;
      return canonicalDrawerRefreshPromise;
    }

    canonicalDrawerRefreshPromise = (async () => {
      await waitForQuickCartIdle();
      if (quickCartInFlight || quickCartPending.size) {
        canonicalDrawerRefreshPending = true;
        return { updated: false };
      }

      const sectionResponse = await originalFetch(
        root +
          '?sections=cart-drawer-section&pandamo_refresh=' +
          Date.now(),
        {
          credentials:'same-origin',
          headers:{Accept:'application/json'},
          cache:'no-store'
        }
      );

      if (!sectionResponse.ok) {
        throw new Error('No se pudo refrescar el carrito');
      }

      const payload = await sectionResponse.json();
      if (canonicalDrawerRefreshPending || quickCartInFlight || quickCartPending.size) {
        canonicalDrawerRefreshPending = true;
        return { updated: false };
      }
      const sectionHtml = payload?.['cart-drawer-section'];
      const updated = replaceDrawerInnerFromSection(sectionHtml);

      const cart = await getCart();
      updateHeaderCartBubble(cart);
      syncPandamoCartVisual(cart);

      document.dispatchEvent(
        new CustomEvent('pandamo:drawer-refreshed',{
          detail:{ cart, reason, updated }
        })
      );

      return { cart, updated };
    })();

    try {
      return await canonicalDrawerRefreshPromise;
    } finally {
      canonicalDrawerRefreshPromise = null;
      if (canonicalDrawerRefreshPending) {
        canonicalDrawerRefreshPending = false;
        scheduleDrawerRefresh();
      }
    }
  };

  /* Capture the trash icon before any theme-level delegated handler. */
  document.addEventListener('click',(event) => {
    const target = event.target instanceof Element
      ? event.target.closest('[data-pandamo-remove-line]')
      : null;

    if (!target) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    fastRemoveLine(
      Number(target.getAttribute('data-pandamo-remove-line')),
      target
    );
  },true);

  window.__pandamoFastRemoveLine = fastRemoveLine;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(() => {
        window.setTimeout(() => {
          refreshVisual().catch(() => {});
        }, 90);
      })
      .catch(() => {});
  });

  document.addEventListener('shopify:section:load', () => scheduleVisualRefresh(80));
  document.addEventListener('cart:updated', scheduleDrawerRefresh);
  document.addEventListener('cart:update', () => {
    window.setTimeout(() => refreshVisual().catch(() => {}), 120);
  });
  document.addEventListener('cart:refresh', () => {
    window.setTimeout(() => refreshVisual().catch(() => {}), 120);
  });

  const initialize = () => {
    refreshVisual().catch(() => {});
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
  /* PANDAMO UPSELL GLOBAL V3 START */
  let pandamoUpsellBusy = false;

  const pandamoUpsellButtons = (variantId) =>
    Array.from(
      document.querySelectorAll(
        '[data-pandamo-upsell-add][data-variant-id="' + variantId + '"]'
      )
    );

  const setPandamoUpsellState = (variantId,state,quantity) => {
    const qty=Math.max(0,Number(quantity)||0);

    pandamoUpsellButtons(variantId).forEach((button) => {
      const label=button.querySelector('[data-pandamo-upsell-label]');
      const count=button.closest('[data-pandamo-cart-upsell]')?.querySelector('[data-pandamo-upsell-count]');

      button.dataset.upsellQuantity=String(qty);
      button.classList.toggle('is-loading',state==='loading');
      button.classList.toggle('is-added',state==='added');
      button.classList.toggle('is-on-fire',state==='ready-more' && qty>0);
      button.disabled=state==='loading';

      if(label){
        label.textContent=
          state==='loading' ? 'Agregando...' :
          state==='added' ? '✓ Agregado' :
          qty>0 ? '🔥 Agregar otro' : 'Agregar';
      }

      if(count){
        count.textContent=qty>0 ? '✓ '+qty+' agregado'+(qty===1?'':'s') : '';
        count.classList.toggle('is-hidden',qty<=0);
      }
    });
  };

  const getPandamoUpsellQuantity=(cart,variantId) =>
    (cart?.items||[]).reduce((total,item) => {
      const isUpsell=String(item?.properties?._pandamo_cart_upsell||'')==='1';
      return Number(item?.variant_id)===Number(variantId) && isUpsell
        ? total+(Number(item?.quantity)||0)
        : total;
    },0);

  const refreshDrawerAfterUpsell=async () => {
    const sectionResponse=await originalFetch(
      root+'?sections=cart-drawer-section&pandamo_cart='+Date.now(),
      {
        credentials:'same-origin',
        headers:{Accept:'application/json'},
        cache:'no-store'
      }
    );

    if(!sectionResponse.ok) return false;

    const payload=await sectionResponse.json();
    const updated=replaceDrawerInnerFromSection(
      payload?.['cart-drawer-section']
    );

    window.setTimeout(() => {
      refreshVisual().catch(() => {});
    },40);

    return updated;
  };

  window.__pandamoAddUpsellFromButton = async (button,event) => {
    if(event){
      event.preventDefault();
      event.stopPropagation();
    }

    if(!button || button.disabled || pandamoUpsellBusy) return false;

    const variantId=String(button.dataset.variantId||'');
    if(!variantId) return false;

    pandamoUpsellBusy=true;
    const previousQty=Number(button.dataset.upsellQuantity||0);
    setPandamoUpsellState(variantId,'loading',previousQty);

    try{
      const formData=new FormData();
      formData.append('id',variantId);
      formData.append('quantity','1');
      formData.append('properties[_pandamo_cart_upsell]','1');
      formData.append('properties[_pandamo_bundle_excluded]','1');
      formData.append('properties[_pandamo_bundle_label]','Precio especial · no aplica en 3×1');
      formData.append('sections','cart-drawer-section');
      formData.append('sections_url',window.location.pathname);

      const response=await originalFetch(root+'cart/add.js',{
        method:'POST',
        credentials:'same-origin',
        headers:{Accept:'application/json'},
        body:formData
      });

      const payload=await response.clone().json().catch(() => null);

      if(!response.ok || payload?.status){
        throw new Error(payload?.description||payload?.message||'No se pudo agregar');
      }

      const cart=await getCart();
      const quantity=getPandamoUpsellQuantity(cart,variantId);

      if(quantity<=previousQty){
        throw new Error('El perfume no apareció en el carrito');
      }

      updateHeaderCartBubble(cart);
      syncPandamoCartVisual(cart);
      setPandamoUpsellState(variantId,'added',quantity);

      window.setTimeout(() => {
        setPandamoUpsellState(variantId,'ready-more',quantity);
      },650);

      /* Do not replace the drawer immediately; keep the button interaction snappy.
         Refresh after feedback so PC/mobile do not lose the clicked control mid-animation. */
      window.setTimeout(() => {
        refreshDrawerAfterUpsell().catch(() => {});
      },1050);

      return true;
    }catch(error){
      console.error('[PANDAMO upsell]',error);
      setPandamoUpsellState(variantId,'ready-more',previousQty);

      pandamoUpsellButtons(variantId).forEach((currentButton) => {
        const label=currentButton.querySelector('[data-pandamo-upsell-label]');
        if(label) label.textContent='Error · intenta otra vez';
      });

      window.setTimeout(() => {
        setPandamoUpsellState(variantId,'ready-more',previousQty);
      },1200);

      return false;
    }finally{
      pandamoUpsellBusy=false;
    }
  };

  /* PANDAMO UPSELL GLOBAL V3 END */

}
